import { useEffect, useRef, useState, type FormEvent, type PointerEvent } from 'react';
import { ApiError, api, type SiteInfo } from '../shared/api.ts';
import { navigate } from '../shared/router.ts';
import { trackGloss } from '../shared/theme.ts';
import { ThemePicker } from './ThemePicker.tsx';
import styles from './Splash.module.css';

/**
 * Near-blank by request: a wordmark, one field, and a quiet line for anyone
 * without a code. The surface does the rest.
 */
export function Splash(): React.JSX.Element {
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    api.get<SiteInfo>('/api/site').then(setSite).catch(() => setSite(null));
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || code.trim().length === 0) return;

    setBusy(true);
    setError(null);

    try {
      await api.post('/api/session', { code });
      navigate('/g');
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reach the server. Check your connection and try again.',
      );
      // The field keeps its value: a mistyped character is far more likely
      // than a wrong code, and clearing it would make them retype the lot.
      inputRef.current?.select();
    } finally {
      setBusy(false);
    }
  };

  // The sweep follows the pointer across the whole page, but is painted on the
  // panel, so the highlight reads as a reflection on that surface.
  const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
    if (panelRef.current) trackGloss(panelRef.current, event);
  };

  return (
    <main className={styles.page} onPointerMove={onPointerMove}>
      <div className={styles.themeSlot}>
        <ThemePicker />
      </div>

      <div className={styles.center}>
        <h1 className={styles.wordmark}>{site?.title ?? 'chadq.photo'}</h1>
        {site?.tagline ? <p className={styles.tagline}>{site.tagline}</p> : null}

        <form className={`${styles.panel} gloss lit`} onSubmit={submit} ref={panelRef}>
          <label className={styles.label} htmlFor="access-code">
            Access code
          </label>

          <input
            id="access-code"
            ref={inputRef}
            className={styles.input}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="go"
            aria-invalid={error !== null}
            aria-describedby={error ? 'code-error' : undefined}
            disabled={busy}
          />

          <button className={styles.submit} type="submit" disabled={busy || code.length === 0}>
            {busy ? 'Checking' : 'View gallery'}
          </button>
        </form>

        {/* Reserved height, so an error does not shift the form under the cursor. */}
        <p className={styles.error} id="code-error" role="status">
          {error}
        </p>

        <Contact site={site} />
      </div>
    </main>
  );
}

function Contact({ site }: { site: SiteInfo | null }): React.JSX.Element | null {
  if (!site) return null;

  const channels = [
    site.contactEmail ? { href: `mailto:${site.contactEmail}`, text: site.contactEmail } : null,
    site.contactPhone ? { href: `tel:${site.contactPhone}`, text: site.contactPhone } : null,
    site.contactInstagram
      ? {
          href: `https://instagram.com/${site.contactInstagram.replace(/^@/, '')}`,
          text: `@${site.contactInstagram.replace(/^@/, '')}`,
        }
      : null,
  ].filter((c): c is { href: string; text: string } => c !== null);

  if (channels.length === 0) return null;

  return (
    <footer className={styles.contact}>
      <span>{site.splashNote ?? 'No code?'}</span>
      {channels.map((channel) => (
        <a key={channel.href} className={styles.contactLink} href={channel.href}>
          {channel.text}
        </a>
      ))}
    </footer>
  );
}
